import { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  // eslint-disable-next-line import/named
  createRoute,
  redirect,
  useRouter,
  matchPathname,
} from "@tanstack/react-router";

import { shouldRedirectToAddPassword } from "./onboardingGuard";

import { useAuthenticationOverlay } from "../../components/AuthenticationDialog";
import PunttLayout from "../../components/layouts/puntt";
import PendingComponent from "../../components/PendingComponent";
import { SIDEBAR_STORAGE_NAME } from "../../components/sidebar/SidebarProvider";
import { BASEPATH } from "../../config/env";
import { getGuidelines } from "../../services/brand-guidelines";
import { getFolderTree } from "../../services/folders";
import { useAppSelector } from "../../utils/hooks";
import { logout } from "../../utils/slices/auth";
import { setDrawerOpen } from "../../utils/slices/ui";
import { store } from "../../utils/store";
import { addPasswordRoute } from "../add-password/route";
import { loginRoute } from "../login/route";
import { rootRoute } from "../root/route";
import { ticketsRoute } from "../tickets/route";
import { ticketRoute } from "../tickets/routes/ticket/route";
import { ticketCompatibilityRoute } from "../tickets/routes/ticket_compat";

export const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  async loader({
    context,
  }: {
    context: { queryClient: QueryClient; search: any };
    location: { pathname: string };
  }) {
    const { value: user } = store.getState().auth;
    const { search } = context;

    if (!user?.ticketId) {
      const { queryClient } = context;

      const [folderTree] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ["folders", "tree", search],
          queryFn: getFolderTree,
        }),
        // Prime the cache so canAccessAskPuntt() can read the enterprise's
        // askPunttEnabled toggle synchronously in child route guards and the
        // sidebar. Swallow errors: if the fetch fails the gate falls back to
        // false (cache miss) and the rest of the layout must still load.
        queryClient
          .ensureQueryData({
            queryKey: ["brand-guidelines"],
            queryFn: getGuidelines,
          })
          .catch(() => undefined),
      ]);

      return {
        folderTree,
      };
    }

    return { folderTree: [] };
  },
  beforeLoad({ search, location }) {
    const { value: user } = store.getState().auth;

    // This is used for redirects after login. It must start with a / or else
    // the path won't match so the redirect will fail.
    const baseLocation = location.pathname;

    if (user == null) {
      throw redirect({
        to: loginRoute.to,
        search: {
          redirect: baseLocation,
        },
      });
    }

    const isTicketRoute = matchPathname(
      BASEPATH,
      location.pathname,
      ticketRoute,
    );
    const isTicketCompatRoute = matchPathname(
      BASEPATH,
      location.pathname,
      ticketCompatibilityRoute,
    );
    const isTicketsRoute = matchPathname(
      BASEPATH,
      location.pathname,
      ticketsRoute,
    );
    const folderTokenPresent =
      "folderId" in search &&
      typeof search.folderId === "string" &&
      sessionStorage.getItem(`${search?.folderId}|token`);

    if (
      user.role == null &&
      ((user.ticketId != null &&
        !isTicketRoute &&
        !isTicketCompatRoute &&
        !folderTokenPresent) ||
        (user.folderId != null &&
          !isTicketsRoute &&
          !isTicketRoute &&
          !isTicketCompatRoute))
    ) {
      store.dispatch(logout());
      throw redirect({
        to: loginRoute.to,
        search: {
          redirect: baseLocation,
        },
      });
    }
    const hasStartedOnboardingFlow = !!localStorage.getItem("onboarding_flow");

    if (shouldRedirectToAddPassword(user, hasStartedOnboardingFlow)) {
      throw redirect({
        to: addPasswordRoute.to,
      });
    }

    if (search.redirect) {
      throw redirect({ to: search.redirect });
    }

    store.dispatch(
      setDrawerOpen({
        app: localStorage.getItem(`app_${SIDEBAR_STORAGE_NAME}`) === "true",
        comments:
          (localStorage.getItem(`comments_${SIDEBAR_STORAGE_NAME}`) ??
            "true") === "true",
      }),
    );

    return {
      search: search || {},
    };
  },
  component: AuthLayout,
  pendingComponent: PendingComponent,
});

export function logoutUrl(basepath: string): string {
  if (basepath === "/") basepath = "";
  const redirect = localStorage.getItem("redirect");
  const destination = new URL(location.origin + basepath + loginRoute.to);
  if (redirect) {
    destination.searchParams.append("redirect", redirect);
  }
  return destination.toString();
}

function AuthLayout() {
  useAuthenticationOverlay();
  const router = useRouter();
  const user = useAppSelector((state) => state.auth.value);

  // This is called only when the user logs out
  if (user == null && router.state.location.pathname !== loginRoute.to) {
    // Immediately redirect to prevent any rendering that could cause error messages
    location.assign(logoutUrl(router.basepath));
    // Return null to prevent any rendering while redirecting
    return null;
  }

  // Hide the navigation chrome and other UI elements for the Figma plugin
  // and the Chrome extension.
  if (
    router.state.location.pathname.includes("tickets/figma-plugin") ||
    router.state.location.pathname.includes("tickets/chrome-extension")
  ) {
    return <Outlet />;
  }

  return <PunttLayout />;
}
